import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Normalise whatever someone typed for a date onto the plain 'YYYY-MM-DD'
// the API/Postgres wants. Accepts an Excel date cell, DD/MM/YYYY, DD-MM-YYYY,
// DD.MM.YYYY, 2-digit years, YYYY-MM-DD / YYYY/MM/DD, and raw Excel serials.
// Returns '' when it can't make sense of the value (import then warns rather
// than failing the whole row).
const toISODate = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date && !isNaN(v)) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  if (!s) return '';

  // YYYY-MM-DD or YYYY/MM/DD (optionally followed by a time part)
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;

  // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY — 2- or 4-digit year
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    let d = parseInt(m[1], 10);
    let mo = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    // if it's unambiguously month-first (e.g. 04/28/1983), swap
    if (mo > 12 && d <= 12) { [d, mo] = [mo, d]; }
    if (y < 100) y += (2000 + y > new Date().getFullYear()) ? 1900 : 2000;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return '';
  }

  // Bare Excel serial number (days since 1899-12-30)
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (n > 59 && n < 60000) {
      const dt = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
      if (!isNaN(dt)) return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    }
  }

  return '';
};

// Import template column -> API field, with the date columns needing toISODate.
const PEOPLE_IMPORT_COLUMNS = [
  { header: 'Full Name*',        key: 'full_name',        width: 24 },
  { header: 'PAN Number',        key: 'pan_number',        width: 14 },
  { header: 'Aadhaar Number',    key: 'aadhaar_number',    width: 16 },
  { header: 'Date of Birth',     key: 'date_of_birth',     width: 18, date: true },
  { header: "Father's Name",     key: 'father_name',       width: 22 },
  { header: 'Gender',            key: 'gender',            width: 12 },
  { header: 'Primary Mobile',    key: 'primary_mobile',    width: 15 },
  { header: 'Secondary Mobile',  key: 'secondary_mobile',  width: 15 },
  { header: 'Additional Phone',  key: 'additional_phone',  width: 15 },
  { header: 'Email',             key: 'email',             width: 24 },
  { header: 'Address (Aadhaar)', key: 'address_aadhaar',   width: 28 },
  { header: 'Address (PAN)',     key: 'address_pan',       width: 28 },
  { header: 'Correspondence Address', key: 'address_correspondence', width: 28 },
  { header: 'DIN',               key: 'din',                width: 12 },
  { header: 'DIN Status',        key: 'din_status',        width: 14 },
  { header: 'DPIN',              key: 'dpin',               width: 12 },
  { header: 'DSC Expiry',        key: 'dsc_expiry',        width: 16, date: true },
  { header: 'Bank Name',         key: 'bank_name',         width: 20 },
  { header: 'Bank Account',      key: 'bank_account',      width: 18 },
  { header: 'IFSC Code',         key: 'bank_ifsc',         width: 14 },
  { header: 'Bank Account Type', key: 'bank_account_type', width: 16 },
  { header: 'Client Name (exact name as in Client Master)', key: 'client_name', width: 30 },
  { header: 'Role (Director/Partner/Proprietor etc.)', key: 'role', width: 22 },
  { header: 'Designation',       key: 'designation',       width: 18 },
  { header: 'Since Date',        key: 'since_date',        width: 16, date: true },
  { header: 'Shareholding %',    key: 'shareholding_percent', width: 14 },
  { header: 'Notes',             key: 'notes',              width: 26 },
];

const ROLES = ['Director','Designated Partner','Partner','Proprietor',
  'Trustee','Karta','Coparcener','Governing Member','Secretary','Member','Other'];

const DIN_STATUSES = ['Active','Deactivated','Surrendered','Disqualified','Not Applicable'];

const ROLE_COLORS = {
  'Director':            'bg-blue-50 text-blue-700 border-blue-200',
  'Designated Partner':  'bg-purple-50 text-purple-700 border-purple-200',
  'Partner':             'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Proprietor':          'bg-green-50 text-green-700 border-green-200',
  'Trustee':             'bg-amber-50 text-amber-700 border-amber-200',
  'Karta':               'bg-orange-50 text-orange-700 border-orange-200',
  'Governing Member':    'bg-teal-50 text-teal-700 border-teal-200',
  'Secretary':           'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Member':              'bg-slate-50 text-slate-700 border-slate-200',
  'Other':               'bg-gray-50 text-gray-600 border-gray-200',
};


const EMPTY_FORM = {
  full_name:'', pan_number:'', aadhaar_number:'', date_of_birth:'',
  father_name:'', gender:'', photo_url:'',
  primary_mobile:'', secondary_mobile:'', additional_phone:'', email:'',
  address_aadhaar:'', address_pan:'', address_correspondence:'',
  din:'', din_status:'Not Applicable', dpin:'', dsc_expiry:'',
  bank_name:'', bank_account:'', bank_ifsc:'', bank_account_type:'', notes:''
};

const EMPTY_LINK = { client_id:'', role:'Director', designation:'', since_date:'', shareholding_percent:'', notes:'' };

const Icons = {
  person:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  id:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
  phone:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.18 2 2 0 0 1 3.05 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/></svg>,
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  link:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  export:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  import:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  plus:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  close:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  search:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  din:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  download: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
};

// ── SMART DATE PICKER ─────────────────────────────────────────────────────────
function SmartDatePicker({ label, value, onChange, placeholder = 'dd/mm/yyyy', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev  = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) cells.push({ d: cells.length - first - total + 1, curr: false });
    return cells;
  };
  const selectDay = (d) => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`); setOpen(false);
  };
  const yearRange = Array.from({ length: 12 }, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';

  return (
    <div style={{ position:'relative' }} ref={ref}>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>}
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', outline:'none', boxSizing:'border-box', color: displayVal ? T.text : T.text3 }}>
        <span>{displayVal || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)', zIndex:9999, width:288, overflow:'hidden' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${T.border}` }}>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div style={{ display:'flex', gap:4 }}>
              <button type="button" onClick={() => setMode(m => m==='month'?'day':'month')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background='none'}>{MONTHS[viewMonth]}</button>
              <button type="button" onClick={() => setMode(m => m==='year'?'day':'year')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background='none'}>{viewYear}</button>
            </div>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          {mode==='month' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {MONTHS.map((m,i) => (
                <button key={m} type="button" onClick={() => { setViewMonth(i); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewMonth===i?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewMonth===i?'#fff':T.text2 }}
                  onMouseEnter={e=>{ if(viewMonth!==i) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e=>{ if(viewMonth!==i) e.currentTarget.style.background='none'; }}>{m}</button>
              ))}
            </div>
          )}
          {mode==='year' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {yearRange.map(y => (
                <button key={y} type="button" onClick={() => { setViewYear(y); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewYear===y?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewYear===y?'#fff':T.text2 }}
                  onMouseEnter={e=>{ if(viewYear!==y) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e=>{ if(viewYear!==y) e.currentTarget.style.background='none'; }}>{y}</button>
              ))}
            </div>
          )}
          {mode==='day' && (
            <div style={{ padding:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:T.text3, padding:'4px 0' }}>{d}</div>)}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
                {getDays().map((cell,i) => {
                  const cd = new Date(viewYear, viewMonth, cell.d); cd.setHours(0,0,0,0);
                  const isSel = selected && cell.curr && cd.getTime()===selected.getTime();
                  const isToday = cell.curr && cd.getTime()===today.getTime();
                  return (
                    <button key={i} type="button" onClick={() => cell.curr && selectDay(cell.d)}
                      style={{ height:32, width:'100%', borderRadius:8, border: isToday&&!isSel?`1px solid #3b82f6`:'none',
                        background: isSel?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                        color: !cell.curr?T.text3:isSel?'#fff':isToday?'#3b82f6':T.text2,
                        fontSize:12, fontWeight: isSel||isToday?700:400, cursor: cell.curr?'pointer':'default' }}
                      onMouseEnter={e=>{ if(cell.curr&&!isSel) e.currentTarget.style.background=T.bg3; }}
                      onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background='none'; }}>{cell.d}
                    </button>
                  );
                })}
              </div>
              {value && (
                <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                  style={{ width:'100%', marginTop:8, padding:'6px 0', fontSize:12, color:T.text3, background:'none', border:'none', cursor:'pointer', borderRadius:8 }}
                  onMouseEnter={e=>{ e.currentTarget.style.color='#ef4444'; e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.color=T.text3; e.currentTarget.style.background='none'; }}>Clear date</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ num, title, T: theme }) {
  const T = theme || { border:'#e8edf5', text3:'#94a3b8' };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'24px 0 16px' }}>
      <div style={{ width:26, height:26, borderRadius:7, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>{num}</div>
      <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>{title}</h4>
      <div style={{ flex:1, height:1, background:T.border }}/>
    </div>
  );
}

function Field({ label, children, T: theme, col }) {
  const T = theme || { text3:'#94a3b8' };
  return (
    <div style={ col ? { gridColumn:`span ${col}` } : {} }>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>}
      {children}
    </div>
  );
}
function People({ token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const headers = { 'Authorization': `Bearer ${token}` };
  const jh = { 'Content-Type': 'application/json', ...headers };

  const [persons, setPersons]   = useState([]);
  const [clients, setClients]   = useState([]);
  const [stats,   setStats]     = useState({ total:0, by_role:[] });
  const [loading, setLoading]   = useState(true);

  // UI state
  const [search,       setSearch]       = useState('');
  const [filterRole,   setFilterRole]   = useState('all');
  const [filterClient, setFilterClient] = useState('all');

  // Panels
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [detailTab,      setDetailTab]      = useState('personal');
  const [showForm,       setShowForm]       = useState(false);
  const [editPerson,     setEditPerson]     = useState(null);
  const [showImport,     setShowImport]     = useState(false);

  // Form state
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);

  // Link form
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm,     setLinkForm]     = useState(EMPTY_LINK);
  const [linkSaving,   setLinkSaving]   = useState(false);

  // Import state
  const [importRows,    setImportRows]    = useState([]);
  const [importResult,  setImportResult]  = useState(null);
  const [importing,     setImporting]     = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { current, total, phase, ok, skip, fail }
  const importFileRef = useRef(null);


  // ── DATA FETCH ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, sRes] = await Promise.all([
        fetch(`${BASE}/api/persons`, { headers }),
        fetch(`${BASE}/api/clients`, { headers }),
        fetch(`${BASE}/api/persons/stats`, { headers }),
      ]);
      const [pData, cData, sData] = await Promise.all([pRes.json(), cRes.json(), sRes.json()]);
      setPersons(Array.isArray(pData) ? pData : []);
      setClients(Array.isArray(cData) ? cData : []);
      setStats(sData || { total:0, by_role:[] });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── FILTER ──────────────────────────────────────────────────────────────────
  const filtered = persons.filter(p => {
    const linked = Array.isArray(p.linked_clients) ? p.linked_clients : [];
    const q = search.toLowerCase();
    const matchSearch = !q || p.full_name?.toLowerCase().includes(q)
      || p.pan_number?.toLowerCase().includes(q)
      || p.din?.toLowerCase().includes(q)
      || p.primary_mobile?.includes(q)
      || p.email?.toLowerCase().includes(q)
      || linked.some(l => l.client_name?.toLowerCase().includes(q));
    const matchRole   = filterRole === 'all' || linked.some(l => l.role === filterRole);
    const matchClient = filterClient === 'all' || linked.some(l => String(l.client_id) === filterClient);
    return matchSearch && matchRole && matchClient;
  });

  // One person can hold several roles at the same company (e.g. Director +
  // Shareholder) — that's several client_persons rows. Collapse them into
  // one entry per company for display, merging roles and carrying the
  // shareholding figures.
  const groupLinks = (linkedClients) => {
    const arr = Array.isArray(linkedClients) ? linkedClients : [];
    const byClient = new Map();
    for (const l of arr) {
      const key = String(l.client_id);
      const g = byClient.get(key) || {
        client_id: l.client_id, client_name: l.client_name,
        roles: [], designations: [], link_ids: [],
        shareholding_percent: null, shareholding_count: null,
        since_date: null, is_active: false,
      };
      if (l.role && !g.roles.includes(l.role)) g.roles.push(l.role);
      if (l.designation && !g.designations.includes(l.designation)) g.designations.push(l.designation);
      if (l.link_id != null) g.link_ids.push(l.link_id);
      if (l.shareholding_percent != null && l.shareholding_percent !== '') g.shareholding_percent = l.shareholding_percent;
      if (l.shareholding_count != null && l.shareholding_count !== '') g.shareholding_count = l.shareholding_count;
      if (l.since_date && (!g.since_date || l.since_date < g.since_date)) g.since_date = l.since_date;
      if (l.is_active !== false) g.is_active = true;
      byClient.set(key, g);
    }
    return Array.from(byClient.values());
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const openAdd = () => { setEditPerson(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (p) => {
    setEditPerson(p);
    setForm({
      full_name: p.full_name||'', pan_number: p.pan_number||'',
      aadhaar_number: p.aadhaar_number||'', date_of_birth: p.date_of_birth?.split('T')[0]||'',
      father_name: p.father_name||'', gender: p.gender||'',
      primary_mobile: p.primary_mobile||'', secondary_mobile: p.secondary_mobile||'',
      additional_phone: p.additional_phone||'', email: p.email||'',
      address_aadhaar: p.address_aadhaar||'', address_pan: p.address_pan||'',
      address_correspondence: p.address_correspondence||'',
      din: p.din||'', din_status: p.din_status||'Not Applicable',
      dpin: p.dpin||'', dsc_expiry: p.dsc_expiry?.split('T')[0]||'',
      bank_name: p.bank_name||'', bank_account: p.bank_account||'',
      bank_ifsc: p.bank_ifsc||'', bank_account_type: p.bank_account_type||'',
      notes: p.notes||'', photo_url: p.photo_url||''
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) return alert('Full Name is required');
    setSaving(true);
    try {
      const url = editPerson ? `${BASE}/api/persons/${editPerson.id}` : `${BASE}/api/persons`;
      const method = editPerson ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: jh, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) return alert(data.message || 'Error saving');
      setShowForm(false);
      fetchAll();
      if (selectedPerson?.id === editPerson?.id) setSelectedPerson(data);
    } catch(e) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this person? This will unlink them from all clients.')) return;
    await fetch(`${BASE}/api/persons/${id}`, { method:'DELETE', headers });
    if (selectedPerson?.id === id) setSelectedPerson(null);
    fetchAll();
  };

  // ── LINK / UNLINK ────────────────────────────────────────────────────────────
  const handleLink = async () => {
    if (!linkForm.client_id) return alert('Please select a client');
    setLinkSaving(true);
    try {
      const res = await fetch(`${BASE}/api/persons/${selectedPerson.id}/link`, {
        method:'POST', headers: jh, body: JSON.stringify(linkForm)
      });
      if (res.ok) {
        setShowLinkForm(false); setLinkForm(EMPTY_LINK);
        const updated = await fetch(`${BASE}/api/persons/${selectedPerson.id}`, { headers });
        setSelectedPerson(await updated.json());
        fetchAll();
      }
    } catch(e) { alert('Link error'); }
    finally { setLinkSaving(false); }
  };

  const handleUnlink = async (linkId, skipConfirm) => {
    if (!skipConfirm && !window.confirm('Remove this association?')) return;
    await fetch(`${BASE}/api/persons/${selectedPerson.id}/link/${linkId}`, { method:'DELETE', headers });
    const updated = await fetch(`${BASE}/api/persons/${selectedPerson.id}`, { headers });
    setSelectedPerson(await updated.json());
    fetchAll();
  };

  // ── EXPORT ───────────────────────────────────────────────────────────────────
  const handleExport = () => {
    const rows = filtered.map(p => {
      const linked = Array.isArray(p.linked_clients) ? p.linked_clients : [];
      return {
        'Full Name': p.full_name,
        'PAN Number': p.pan_number||'',
        'Aadhaar Number': p.aadhaar_number||'',
        'Date of Birth': p.date_of_birth ? new Date(p.date_of_birth).toLocaleDateString('en-IN') : '',
        'Father Name': p.father_name||'',
        'Gender': p.gender||'',
        'Primary Mobile': p.primary_mobile||'',
        'Secondary Mobile': p.secondary_mobile||'',
        'Email': p.email||'',
        'DIN': p.din||'',
        'DIN Status': p.din_status||'',
        'DPIN': p.dpin||'',
        'Address (Aadhaar)': p.address_aadhaar||'',
        'Bank Name': p.bank_name||'',
        'Bank Account': p.bank_account||'',
        'IFSC Code': p.bank_ifsc||'',
        'Associated Companies': groupLinks(linked).map(g => `${g.client_name} (${g.roles.join('/')})`).join('; '),
        'Notes': p.notes||'',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [20,14,14,14,18,8,14,14,20,10,14,10,30,20,16,12,40,20].map(w=>({wch:w}));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'People');
    XLSX.writeFile(wb, `People_Export_${new Date().toLocaleDateString('en-IN').replace(/\//g,'-')}.xlsx`);
  };

  const downloadTemplate = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Import Template');
      ws.columns = PEOPLE_IMPORT_COLUMNS.map(c => ({ header: c.header, key: c.key, width: c.width }));

      // Header formatting — bold white text on navy fill, like the rest of the app's templates
      ws.getRow(1).height = 22;
      ws.getRow(1).eachCell(c => {
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1F45' } };
        c.alignment = { vertical: 'middle', horizontal: 'left' };
      });

      // Keep date columns as plain text so Excel doesn't silently reformat them,
      // and explain the accepted formats via a cell note on the header.
      PEOPLE_IMPORT_COLUMNS.filter(c => c.date).forEach(c => {
        const col = ws.getColumn(c.key);
        col.numFmt = '@';
        ws.getCell(`${col.letter}1`).note =
          'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD (2-digit year OK).';
      });
      ws.getCell(`${ws.getColumn('full_name').letter}1`).note = 'Required.';
      ws.getCell(`${ws.getColumn('pan_number').letter}1`).note =
        'Used to match an existing person on re-import/multi-company rows. Leave blank only for a brand-new person with no PAN.';
      ws.getCell(`${ws.getColumn('client_name').letter}1`).note =
        'Must exactly match a client name already in ChanakyaX. Add one row per company the person is linked to — same PAN each time — so all links attach to the same person instead of creating duplicates.';

      ws.addRow({
        full_name: 'Rahul Kumar Sharma', pan_number: 'ABCDE1234F', aadhaar_number: '123456789012',
        date_of_birth: '15/04/1985', father_name: 'Ram Kumar Sharma', gender: 'Male',
        primary_mobile: '9876543210', secondary_mobile: '9876543211', email: 'rahul@email.com',
        address_aadhaar: '12, MG Road, Delhi - 110001',
        din: 'AA12345B', din_status: 'Active',
        client_name: 'ABC Private Limited', role: 'Director', designation: 'Managing Director',
        since_date: '01/01/2020', shareholding_percent: '25.00',
      });
      // Second sample row: same person (same PAN), a different company, no shareholding there —
      // shows how one person links to multiple companies without duplicating the person record.
      ws.addRow({
        full_name: 'Rahul Kumar Sharma', pan_number: 'ABCDE1234F',
        client_name: 'XYZ LLP', role: 'Designated Partner', designation: '',
        since_date: '01/06/2021', shareholding_percent: '',
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'People_Import_Template.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setImportResult({ success: 0, errors: [], message: `Could not generate template: ${e.message}` });
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const DATE_FIELDS = new Set(PEOPLE_IMPORT_COLUMNS.filter(c => c.date).map(c => c.key));
        const HEADER_TO_KEY = {};
        PEOPLE_IMPORT_COLUMNS.forEach(c => {
          HEADER_TO_KEY[c.header.replace(/\s*\([^)]*\)/g,'').replace(/\*/g,'').trim().toLowerCase()] = c.key;
        });
        const normalized = data.map(row => {
          const out = {};
          Object.entries(row).forEach(([k, v]) => {
            const cleanLabel = k.replace(/\s*\([^)]*\)/g,'').replace(/\*/g,'').trim().toLowerCase();
            const field = HEADER_TO_KEY[cleanLabel] || cleanLabel.replace(/\s+/g,'_');
            if (DATE_FIELDS.has(field)) {
              const iso = toISODate(v);
              out[field] = iso;
              const rawStr = v instanceof Date ? '' : String(v).trim();
              if (!iso && rawStr) out[`${field}_raw`] = rawStr; // couldn't parse — kept so the row error is specific
            } else {
              out[field] = String(v).trim();
            }
          });
          return out;
        });
        setImportRows(normalized);
        setImportResult(null);
      } catch (err) {
        setImportRows([]);
        setImportResult({ success: 0, errors: [], message: `Could not read this file: ${err.message}. Make sure it's a valid .xlsx/.xls exported from the template.` });
      }
    };
    reader.onerror = () => setImportResult({ success: 0, errors: [], message: 'Could not read the selected file.' });
    reader.readAsBinaryString(file);
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) return;
    // Catch unparseable dates before hitting the server, with a precise row number.
    const dateErrors = [];
    importRows.forEach((row, i) => {
      PEOPLE_IMPORT_COLUMNS.filter(c => c.date).forEach(c => {
        if (row[`${c.key}_raw`]) dateErrors.push(`Row ${i+2}: "${c.header}" value "${row[`${c.key}_raw`]}" isn't a recognisable date — use DD/MM/YYYY, DD-MM-YYYY or YYYY-MM-DD`);
      });
    });
    if (dateErrors.length) { setImportResult({ success: 0, errors: dateErrors }); return; }

    setImporting(true);
    setImportResult(null);
    setImportProgress({ current: 0, total: importRows.length, phase: 'preparing' });

    let success = 0, skipped = 0;
    const errors = [];
    const warnings = [];
    // Rows are sent one at a time (instead of one bulk call) so the UI can
    // show real per-row progress, same as the Client module's importer.
    const tick = (i) => setImportProgress({
      current: i + 1, total: importRows.length, phase: 'importing',
      ok: success, skip: skipped, fail: errors.length,
    });
    try {
      for (const [i, row] of importRows.entries()) {
        const rowNum = i + 2;
        try {
          const res = await fetch(`${BASE}/api/persons/import`, {
            method:'POST', headers: jh, body: JSON.stringify({ rows: [row] })
          });
          let data;
          try { data = await res.json(); } catch { data = null; }
          if (!res.ok) {
            errors.push(`Row ${rowNum}: ${(data && data.message) || `HTTP ${res.status}`}`);
          } else if (data && data.success > 0) {
            success++;
          } else {
            const raw = (data && data.errors && data.errors[0]) || 'Import failed';
            const msg = raw.replace(/^Row\s+\d+:\s*/, '');
            if (/not found/i.test(msg)) { skipped++; warnings.push(`Row ${rowNum}: ${msg}`); }
            else errors.push(`Row ${rowNum}: ${msg}`);
          }
        } catch (e) {
          errors.push(`Row ${rowNum}: ${e.message}`);
        }
        tick(i);
      }
      setImportResult({ success, skipped, errors, warnings, total: importRows.length });
      if (success > 0) fetchAll();
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  // ── AVATAR ───────────────────────────────────────────────────────────────────
  const avatarColors = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899'];
  const avatarColor = (name) => avatarColors[(name?.charCodeAt(0)||0) % avatarColors.length];

  // ── RENDER ───────────────────────────────────────────────────────────────────

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  const roleBadge = (role) => {
    const map = {
      Director:    { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff',   border: darkMode?'rgba(37,99,235,0.25)':'#bfdbfe'  },
      Partner:     { c:'#7c3aed', bg: darkMode?'rgba(124,58,237,0.12)':'#f5f3ff', border: darkMode?'rgba(124,58,237,0.25)':'#ddd6fe' },
      'Designated Partner': { c:'#6d28d9', bg: darkMode?'rgba(109,40,217,0.14)':'#f5f3ff', border: darkMode?'rgba(109,40,217,0.3)':'#c4b5fd' },
      Proprietor:  { c:'#0d9488', bg: darkMode?'rgba(13,148,136,0.12)':'#f0fdfa', border: darkMode?'rgba(13,148,136,0.25)':'#99f6e4' },
      Trustee:     { c:'#d97706', bg: darkMode?'rgba(217,119,6,0.12)':'#fffbeb',   border: darkMode?'rgba(217,119,6,0.25)':'#fde68a'  },
      Shareholder: { c:'#db2777', bg: darkMode?'rgba(219,39,119,0.12)':'#fdf2f8', border: darkMode?'rgba(219,39,119,0.25)':'#fbcfe8' },
      Karta:       { c:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed',  border: darkMode?'rgba(249,115,22,0.25)':'#fed7aa' },
    };
    return map[role] || { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc', border: darkMode?'rgba(100,116,139,0.25)':'#e2e8f0' };
  };

  // ── RENDER ──
  return (
    <div style={{ display:'flex', minHeight:'100vh',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff',
    }}>

      {/* ── MAIN PANEL ── */}
      <div style={{ display:'flex', flexDirection:'column', flex:1, minWidth:0 }}>

        {/* Header */}
        <div style={{ padding:'24px 28px 20px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <div>
              <p style={{ fontSize:13, color:T.text3, margin:0 }}>
                {stats.total} persons · Directors, Partners, Trustees &amp; more
              </p>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button onClick={() => setShowImport(true)} style={{
                display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9,
                border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
              }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='#f97316';e.currentTarget.style.color='#f97316';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border2;e.currentTarget.style.color=T.text2;}}>
                {Icons.import} Import
              </button>
              <button onClick={handleExport} style={{
                display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9,
                border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
              }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='#f97316';e.currentTarget.style.color='#f97316';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border2;e.currentTarget.style.color=T.text2;}}>
                {Icons.export} Export
              </button>
              <button onClick={openAdd} style={{
                display:'flex', alignItems:'center', gap:6, padding:'8px 18px', borderRadius:9,
                border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
                fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
              }}>
                {Icons.plus} Add Person
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:18 }}>
            {[
              { label:'Total Persons', value: stats.total, color:'#3b82f6', glow:'rgba(59,130,246,0.12)' },
              ...(stats.by_role||[]).slice(0,4).map(r => ({ label: r.role, value: r.count, color:'#6366f1', glow:'rgba(99,102,241,0.12)' }))
            ].map((s,i) => (
              <div key={i} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'14px 18px', boxShadow:`0 4px 12px ${s.glow}`, position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:-12, right:-12, width:40, height:40, borderRadius:'50%', background:s.glow, filter:'blur(8px)' }}/>
                <p style={{ fontSize:20, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
                <p style={{ fontSize:11, color:T.text3, marginTop:4, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:'12px 16px', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div style={{ position:'relative', flex:1, minWidth:192 }}>
              <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}>{Icons.search}</span>
              <input type="text" placeholder="Search by name, PAN, DIN, mobile, email, company..."
                value={search} onChange={e=>setSearch(e.target.value)}
                style={{ ...is, paddingLeft:32 }} />
            </div>
            <select value={filterRole} onChange={e=>setFilterRole(e.target.value)}
              style={{ border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, minWidth:140 }}>
              <option value="all">All Roles</option>
              {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <select value={filterClient} onChange={e=>setFilterClient(e.target.value)}
              style={{ border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, minWidth:160 }}>
              <option value="all">All Clients</option>
              {clients.map(c=><option key={c.id} value={String(c.id)}>{c.client_name}</option>)}
            </select>
            {(search||filterRole!=='all'||filterClient!=='all') && (
              <button onClick={()=>{setSearch('');setFilterRole('all');setFilterClient('all');}}
                style={{ fontSize:12, color:'#ef4444', padding:'7px 12px', borderRadius:9, border:'1px solid #fecaca', background:'none', cursor:'pointer', fontWeight:500 }}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ flex:1, overflow:'auto', padding:'0 28px 28px', scrollbarWidth:'thin' }}>
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
            {loading ? (
              <div style={{ padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading persons...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding:'64px 0', textAlign:'center' }}>
                <div style={{ width:64, height:64, background:T.bg3, borderRadius:16, border:`2px dashed ${T.border2}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No persons found</p>
                <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>Add your first person or adjust filters</p>
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['Person','PAN / Aadhaar','DIN / DPIN','Contact','Associated Companies','Actions'].map(h=>(
                      <th key={h} style={{ padding:'11px 20px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p,i) => {
                    const linked = Array.isArray(p.linked_clients) ? p.linked_clients : [];
                    const isSel = selectedPerson?.id === p.id;
                    return (
                      <tr key={p.id} onClick={()=>{ setSelectedPerson(p); setDetailTab('personal'); }}
                        style={{ borderBottom:`1px solid ${T.border}`, cursor:'pointer', transition:'background 0.15s',
                          background: isSel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode&&i%2!==0?T.bg3:'transparent') }}
                        onMouseEnter={ev=>{ if(!isSel) ev.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                        onMouseLeave={ev=>{ ev.currentTarget.style.background=isSel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode&&i%2!==0?T.bg3:'transparent'); }}>

                        <td style={{ padding:'12px 20px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                            {p.photo_url ? (
                              <img src={p.photo_url} alt={p.full_name} style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:`1px solid ${T.border}` }} />
                            ) : (
                              <div style={{ width:36, height:36, borderRadius:'50%', background: avatarColor(p.full_name), display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:700, flexShrink:0 }}>
                                {p.full_name?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{p.full_name}</p>
                              {p.gender && <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{p.gender}{p.date_of_birth ? ` · DOB: ${new Date(p.date_of_birth).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}` : ''}</p>}
                            </div>
                          </div>
                        </td>

                        <td style={{ padding:'12px 20px' }}>
                          {p.pan_number && <p style={{ fontSize:13, fontFamily:'monospace', fontWeight:700, color:T.text, margin:0 }}>{p.pan_number}</p>}
                          {p.aadhaar_number && <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:'3px 0 0' }}>{p.aadhaar_number}</p>}
                          {!p.pan_number && !p.aadhaar_number && <span style={{ fontSize:11, color:T.text3 }}>—</span>}
                        </td>

                        <td style={{ padding:'12px 20px' }}>
                          {p.din && (
                            <div>
                              <p style={{ fontSize:13, fontFamily:'monospace', fontWeight:700, color:T.text, margin:0 }}>DIN: {p.din}</p>
                              <span style={{ fontSize:11, fontWeight:600, padding:'2px 7px', borderRadius:20, marginTop:3, display:'inline-block',
                                color: p.din_status==='Active'?'#16a34a':p.din_status==='Deactivated'?'#dc2626':T.text3,
                                background: p.din_status==='Active'?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):p.din_status==='Deactivated'?(darkMode?'rgba(220,38,38,0.12)':'#fef2f2'):(darkMode?T.bg3:'#f8fafc') }}>
                                {p.din_status}
                              </span>
                            </div>
                          )}
                          {p.dpin && <p style={{ fontSize:12, fontFamily:'monospace', color:T.text2, margin:'3px 0 0' }}>DPIN: {p.dpin}</p>}
                          {!p.din && !p.dpin && <span style={{ fontSize:11, color:T.text3 }}>—</span>}
                        </td>

                        <td style={{ padding:'12px 20px' }}>
                          {p.primary_mobile && <p style={{ fontSize:13, color:T.text2, margin:0 }}>{p.primary_mobile}</p>}
                          {p.email && <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:144 }}>{p.email}</p>}
                          {!p.primary_mobile && !p.email && <span style={{ fontSize:11, color:T.text3 }}>—</span>}
                        </td>

                        <td style={{ padding:'12px 20px' }}>
                          {(() => {
                            const groups = groupLinks(linked);
                            if (groups.length === 0) return <span style={{ fontSize:11, color:T.text3 }}>Not linked</span>;
                            return (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:5, maxWidth:256 }}>
                                {groups.slice(0,2).map((g,li) => {
                                  const rb = roleBadge(g.roles[0]);
                                  return (
                                    <span key={li} title={g.roles.join(' + ')} style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:rb.c, background:rb.bg, border:`1px solid ${rb.border}` }}>
                                      {g.client_name}
                                    </span>
                                  );
                                })}
                                {groups.length > 2 && (
                                  <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:T.text3, background:T.bg3 }}>
                                    +{groups.length-2} more
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        <td style={{ padding:'12px 20px' }} onClick={e=>e.stopPropagation()}>
                          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <button onClick={()=>openEdit(p)}
                              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                              onMouseEnter={e2=>{e2.currentTarget.style.background=T.bg3;e2.currentTarget.style.color=T.text2;}}
                              onMouseLeave={e2=>{e2.currentTarget.style.background='none';e2.currentTarget.style.color=T.text3;}}>
                              {Icons.edit}
                            </button>
                            <button onClick={()=>handleDelete(p.id)}
                              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                              onMouseEnter={e2=>e2.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                              onMouseLeave={e2=>e2.currentTarget.style.background='none'}>
                              {Icons.trash}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── DETAIL PANEL ── */}
      {selectedPerson && (
        <div style={{ width:384, background:T.bg2, borderLeft:`1px solid ${T.border}`, display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden', minHeight:'100vh', boxShadow: darkMode?'-8px 0 32px rgba(0,0,0,0.3)':'none' }}>

          {/* Panel header */}
          <div style={{ padding:'16px 20px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              {selectedPerson.photo_url ? (
                <img src={selectedPerson.photo_url} alt={selectedPerson.full_name} style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'2px solid rgba(255,255,255,0.3)' }} />
              ) : (
                <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:18, fontWeight:700, flexShrink:0 }}>
                  {selectedPerson.full_name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div>
                <p style={{ fontSize:14, fontWeight:700, color:'#fff', margin:0 }}>{selectedPerson.full_name}</p>
                {selectedPerson.pan_number && <p style={{ fontSize:11, color:'rgba(255,255,255,0.6)', fontFamily:'monospace', margin:'3px 0 0' }}>{selectedPerson.pan_number}</p>}
              </div>
            </div>
            <button onClick={()=>setSelectedPerson(null)}
              style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
              {Icons.close}
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2 }}>
            {[
              { key:'personal', label:'Personal', icon: Icons.person },
              { key:'contact',  label:'Contact',  icon: Icons.phone },
              { key:'corp',     label:'Corp. IDs', icon: Icons.din },
              { key:'links',    label:'Companies', icon: Icons.building },
            ].map(tab => (
              <button key={tab.key} onClick={()=>setDetailTab(tab.key)} style={{
                flex:1, padding:'10px 0', display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                fontSize:11, fontWeight:600, border:'none', cursor:'pointer',
                borderBottom: detailTab===tab.key?'2px solid #3b82f6':'2px solid transparent',
                color: detailTab===tab.key?'#3b82f6':T.text3, background:'none', transition:'all 0.15s',
              }}
              onMouseEnter={e=>{ if(detailTab!==tab.key) e.currentTarget.style.color=T.text2; }}
              onMouseLeave={e=>{ if(detailTab!==tab.key) e.currentTarget.style.color=T.text3; }}>
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex:1, overflowY:'auto', padding:20, background:T.bg2, scrollbarWidth:'thin' }}>

            {/* PERSONAL */}
            {detailTab === 'personal' && (
              <div>
                {[
                  { label:'Full Name',    value: selectedPerson.full_name },
                  { label:'PAN Number',   value: selectedPerson.pan_number, mono:true },
                  { label:'Aadhaar',      value: selectedPerson.aadhaar_number, mono:true },
                  { label:'Date of Birth',value: selectedPerson.date_of_birth ? new Date(selectedPerson.date_of_birth).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : null },
                  { label:"Father's Name",value: selectedPerson.father_name },
                  { label:'Gender',       value: selectedPerson.gender },
                ].filter(r=>r.value).map(r=>(
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:11, color:T.text3, fontWeight:500, width:120, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontSize:13, color:T.text, fontWeight:500, textAlign:'right', fontFamily: r.mono?'monospace':'inherit' }}>{r.value}</span>
                  </div>
                ))}
                {selectedPerson.notes && (
                  <div style={{ marginTop:12, padding:12, background:T.bg3, borderRadius:10, border:`1px solid ${T.border}` }}>
                    <p style={{ fontSize:11, color:T.text3, fontWeight:600, marginBottom:6 }}>Notes</p>
                    <p style={{ fontSize:13, color:T.text2, lineHeight:1.5, margin:0 }}>{selectedPerson.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* CONTACT */}
            {detailTab === 'contact' && (
              <div>
                {[
                  { label:'Primary Mobile',   value: selectedPerson.primary_mobile },
                  { label:'Secondary Mobile', value: selectedPerson.secondary_mobile },
                  { label:'Additional Phone', value: selectedPerson.additional_phone },
                  { label:'Email',            value: selectedPerson.email },
                ].filter(r=>r.value).map(r=>(
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:11, color:T.text3, fontWeight:500, width:140, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontSize:13, color:T.text, fontWeight:500, textAlign:'right', wordBreak:'break-all' }}>{r.value}</span>
                  </div>
                ))}
                {[
                  { label:'Address (Aadhaar)', value: selectedPerson.address_aadhaar },
                  { label:'Address (PAN)',      value: selectedPerson.address_pan },
                  { label:'Correspondence',     value: selectedPerson.address_correspondence },
                ].filter(r=>r.value).map(r=>(
                  <div key={r.label} style={{ marginTop:12 }}>
                    <p style={{ fontSize:11, color:T.text3, fontWeight:600, marginBottom:6 }}>{r.label}</p>
                    <p style={{ fontSize:13, color:T.text2, background:T.bg3, padding:12, borderRadius:10, border:`1px solid ${T.border}`, lineHeight:1.6, margin:0 }}>{r.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* CORPORATE IDs */}
            {detailTab === 'corp' && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {selectedPerson.din && (
                  <div style={{ background: darkMode?'rgba(37,99,235,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(37,99,235,0.2)':'#bfdbfe'}`, borderRadius:12, padding:16 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                      <p style={{ fontSize:10, fontWeight:700, color:'#2563eb', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>DIN</p>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                        color: selectedPerson.din_status==='Active'?'#16a34a':selectedPerson.din_status==='Deactivated'?'#dc2626':T.text3,
                        background: selectedPerson.din_status==='Active'?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):selectedPerson.din_status==='Deactivated'?(darkMode?'rgba(220,38,38,0.12)':'#fef2f2'):(darkMode?T.bg3:'#f8fafc') }}>
                        {selectedPerson.din_status}
                      </span>
                    </div>
                    <p style={{ fontSize:18, fontWeight:800, fontFamily:'monospace', color:T.text, margin:0 }}>{selectedPerson.din}</p>
                    <p style={{ fontSize:11, color:'#3b82f6', marginTop:6 }}>Fetch from MCA — Coming Soon</p>
                  </div>
                )}
                {selectedPerson.dpin && (
                  <div style={{ background: darkMode?'rgba(124,58,237,0.1)':'#f5f3ff', border:`1px solid ${darkMode?'rgba(124,58,237,0.2)':'#ddd6fe'}`, borderRadius:12, padding:16 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 8px' }}>DPIN</p>
                    <p style={{ fontSize:18, fontWeight:800, fontFamily:'monospace', color:T.text, margin:0 }}>{selectedPerson.dpin}</p>
                  </div>
                )}
                {selectedPerson.dsc_expiry && (
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:11, color:T.text3 }}>DSC Expiry</span>
                    <span style={{ fontSize:13, fontWeight:500, color: new Date(selectedPerson.dsc_expiry)<new Date()?'#dc2626':T.text }}>
                      {new Date(selectedPerson.dsc_expiry).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                      {new Date(selectedPerson.dsc_expiry)<new Date()?' (Expired)':''}
                    </span>
                  </div>
                )}
                {selectedPerson.bank_account && (
                  <div style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:14, marginTop:4 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Bank Details</p>
                    {[
                      { label:'Bank',    value: selectedPerson.bank_name },
                      { label:'Account', value: selectedPerson.bank_account, mono:true },
                      { label:'IFSC',    value: selectedPerson.bank_ifsc, mono:true },
                      { label:'Type',    value: selectedPerson.bank_account_type },
                    ].filter(r=>r.value).map(r=>(
                      <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0' }}>
                        <span style={{ fontSize:11, color:T.text3 }}>{r.label}</span>
                        <span style={{ fontSize:13, fontWeight:500, color:T.text, fontFamily: r.mono?'monospace':'inherit' }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {!selectedPerson.din && !selectedPerson.dpin && !selectedPerson.dsc_expiry && !selectedPerson.bank_account && (
                  <div style={{ padding:'40px 0', textAlign:'center', color:T.text3 }}>
                    <p style={{ fontSize:13, margin:0 }}>No corporate IDs added yet</p>
                    <button onClick={()=>openEdit(selectedPerson)} style={{ fontSize:12, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', marginTop:8 }}>Edit to add</button>
                  </div>
                )}
              </div>
            )}

            {/* ASSOCIATED COMPANIES */}
            {detailTab === 'links' && (() => {
              const linkGroups = groupLinks(selectedPerson.linked_clients);
              return (
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>
                    {linkGroups.length} {linkGroups.length === 1 ? 'Company' : 'Companies'}
                  </p>
                  <button onClick={()=>setShowLinkForm(s=>!s)} style={{
                    display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:500, padding:'6px 12px', borderRadius:8, cursor:'pointer',
                    border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`,
                    background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', color:'#3b82f6',
                  }}>
                    {Icons.plus} Link Company
                  </button>
                </div>

                {showLinkForm && (
                  <div style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:16, display:'flex', flexDirection:'column', gap:12 }}>
                    <p style={{ fontSize:12, fontWeight:700, color:T.text, margin:0 }}>New Association</p>
                    <div>
                      <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:5 }}>Client *</label>
                      <select value={linkForm.client_id} onChange={e=>setLinkForm(f=>({...f,client_id:e.target.value}))}
                        style={{ ...is, fontSize:12 }}>
                        <option value="">Select client...</option>
                        {clients.map(c=><option key={c.id} value={c.id}>{c.client_name}</option>)}
                      </select>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:5 }}>Role</label>
                        <select value={linkForm.role} onChange={e=>setLinkForm(f=>({...f,role:e.target.value}))}
                          style={{ ...is, fontSize:12 }}>
                          {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:5 }}>Since Date</label>
                        <input type="date" value={linkForm.since_date} onChange={e=>setLinkForm(f=>({...f,since_date:e.target.value}))}
                          style={{ ...is, fontSize:12 }} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:5 }}>Designation</label>
                        <input type="text" placeholder="e.g. Managing Director" value={linkForm.designation}
                          onChange={e=>setLinkForm(f=>({...f,designation:e.target.value}))} style={{ ...is, fontSize:12 }} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:5 }}>Shareholding %</label>
                        <input type="number" placeholder="25.00" value={linkForm.shareholding_percent}
                          onChange={e=>setLinkForm(f=>({...f,shareholding_percent:e.target.value}))} style={{ ...is, fontSize:12 }} />
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={()=>setShowLinkForm(false)} style={{ flex:1, padding:'8px 0', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, fontSize:12, color:T.text2, cursor:'pointer' }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                        onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
                      <button onClick={handleLink} disabled={linkSaving} style={{ flex:1, padding:'8px 0', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', opacity:linkSaving?0.6:1 }}>
                        {linkSaving ? 'Saving...' : 'Save Link'}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {linkGroups.length === 0 ? (
                    <p style={{ fontSize:13, color:T.text3, textAlign:'center', padding:'32px 0' }}>Not linked to any client yet</p>
                  ) : linkGroups.map((g,i) => {
                    return (
                      <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:14, background: g.is_active?T.bg2:(darkMode?'rgba(255,255,255,0.03)':'#fafafa'), opacity: g.is_active?1:0.6, transition:'background 0.15s' }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                        onMouseLeave={e=>e.currentTarget.style.background=g.is_active?T.bg2:(darkMode?'rgba(255,255,255,0.03)':'#fafafa')}>
                        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.client_name}</p>
                            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:6, flexWrap:'wrap' }}>
                              {g.roles.map(r => {
                                const rb = roleBadge(r);
                                return <span key={r} style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:rb.c, background:rb.bg, border:`1px solid ${rb.border}` }}>{r}</span>;
                              })}
                              {g.designations.length > 0 && <span style={{ fontSize:11, color:T.text3 }}>{g.designations.join(', ')}</span>}
                            </div>
                            {(g.shareholding_count != null || g.shareholding_percent != null) && (
                              <p style={{ fontSize:11, color:T.text3, marginTop:6 }}>
                                Shareholding:
                                {g.shareholding_count != null && ` ${Number(g.shareholding_count).toLocaleString('en-IN')} shares`}
                                {g.shareholding_count != null && g.shareholding_percent != null && ' ·'}
                                {g.shareholding_percent != null && ` ${g.shareholding_percent}%`}
                              </p>
                            )}
                            {g.since_date && <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Since {new Date(g.since_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>}
                          </div>
                          <button onClick={()=>{ if (window.confirm('Remove all associations with this company?')) g.link_ids.forEach(id => handleUnlink(id, true)); }}
                            style={{ width:26, height:26, borderRadius:7, border:'none', background:'none', color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', marginLeft:8, flexShrink:0 }}
                            onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                            onMouseLeave={e=>e.currentTarget.style.background='none'}>
                            {Icons.trash}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ); })()}
          </div>

          {/* Detail footer */}
          <div style={{ padding:'12px 16px', borderTop:`1px solid ${T.border}`, display:'flex', gap:8, flexShrink:0, background:T.bg2 }}>
            <button onClick={()=>openEdit(selectedPerson)} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'9px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
              onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>
              {Icons.edit} Edit
            </button>
            <button onClick={()=>handleDelete(selectedPerson.id)} style={{ padding:'9px 14px', borderRadius:10, border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}`, background: darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
              onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fee2e2'}
              onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.08)':'#fef2f2'}>
              {Icons.trash}
            </button>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:920, height:'92vh', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 32px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', flexShrink:0 }}>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{editPerson ? 'Edit Person' : 'Add New Person'}</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>Complete personal and corporate information</p>
              </div>
              <button onClick={()=>setShowForm(false)}
                style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'8px 32px 24px', background:T.bg3, scrollbarWidth:'none', msOverflowStyle:'none' }}>

              <SectionHeader num="1" title="Identity" T={T} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                {/* Profile photo */}
                <div style={{ gridColumn:'span 3', display:'flex', alignItems:'center', gap:20, paddingBottom:16, marginBottom:4, borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ position:'relative', flexShrink:0 }}>
                    {form.photo_url ? (
                      <img src={form.photo_url} alt="Profile" style={{ width:80, height:80, borderRadius:'50%', objectFit:'cover', border:`2px solid ${T.border}` }} />
                    ) : (
                      <div style={{ width:80, height:80, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', border:`2px dashed ${T.border2}`, background:T.bg2 }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                    )}
                    <label style={{ position:'absolute', bottom:-3, right:-3, width:26, height:26, borderRadius:'50%', background:T.bg2, border:`2px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                      <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.size > 2*1024*1024) return alert('Image must be under 2MB');
                        const reader = new FileReader();
                        reader.onload = ev => setForm(f => ({ ...f, photo_url: ev.target.result }));
                        reader.readAsDataURL(file);
                      }} />
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="2.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    </label>
                  </div>
                  <div>
                    <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>Profile Photo</p>
                    <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>JPG or PNG · Max 2MB</p>
                    {form.photo_url && <button type="button" onClick={() => setForm(f => ({ ...f, photo_url:'' }))} style={{ fontSize:12, color:'#ef4444', background:'none', border:'none', cursor:'pointer', marginTop:6, padding:0 }}>Remove photo</button>}
                  </div>
                </div>

                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Full Name (as per PAN) *</label>
                  <input type="text" placeholder="Rahul Kumar Sharma" value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Gender</label>
                  <select value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))} style={is}>
                    <option value="">Select...</option><option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>PAN Number</label>
                  <input type="text" placeholder="ABCDE1234F" maxLength={10} value={form.pan_number} onChange={e=>setForm(f=>({...f,pan_number:e.target.value.toUpperCase()}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Aadhaar Number</label>
                  <input type="text" placeholder="123456789012" maxLength={12} value={form.aadhaar_number} onChange={e=>setForm(f=>({...f,aadhaar_number:e.target.value.replace(/\D/g,'')}))} style={is} />
                </div>
                <SmartDatePicker label="Date of Birth (as per PAN)" value={form.date_of_birth} onChange={v=>setForm(f=>({...f,date_of_birth:v}))} placeholder="Select date of birth" darkMode={darkMode} T={T} />
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Father's Name</label>
                  <input type="text" placeholder="Ram Kumar Sharma" value={form.father_name} onChange={e=>setForm(f=>({...f,father_name:e.target.value}))} style={is} />
                </div>
              </div>

              <SectionHeader num="2" title="Contact Information" T={T} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                {[
                  { label:'Primary Mobile', key:'primary_mobile', ph:'9876543210', max:15 },
                  { label:'Secondary Mobile', key:'secondary_mobile', ph:'9876543211', max:15 },
                  { label:'Additional Phone', key:'additional_phone', ph:'Landline / Other', max:15 },
                ].map(f=>(
                  <div key={f.key}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                    <input type="text" placeholder={f.ph} maxLength={f.max} value={form[f.key]} onChange={e=>setForm(fm=>({...fm,[f.key]:e.target.value}))} style={is} />
                  </div>
                ))}
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Email ID</label>
                  <input type="email" placeholder="rahul@example.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={is} />
                </div>
              </div>

              <SectionHeader num="3" title="Address" T={T} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:16 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Address (as per Aadhaar)</label>
                  <textarea rows="3" placeholder="Enter address as per Aadhaar..." value={form.address_aadhaar} onChange={e=>setForm(f=>({...f,address_aadhaar:e.target.value}))} style={{ ...is, resize:'none' }} />
                </div>
              </div>

              <SectionHeader num="4" title="Corporate Identifiers" T={T} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>DIN / DPIN</label>
                  <input type="text" placeholder="AA123456" maxLength={8} value={form.din} onChange={e=>setForm(f=>({...f,din:e.target.value.toUpperCase()}))} style={is} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Director Identification Number</p>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>DIN Status</label>
                  <select value={form.din_status} onChange={e=>setForm(f=>({...f,din_status:e.target.value}))} style={is}>
                    {DIN_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <SmartDatePicker label="DSC Expiry" value={form.dsc_expiry} onChange={v=>setForm(f=>({...f,dsc_expiry:v}))} placeholder="Select DSC expiry" darkMode={darkMode} T={T} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>If this person is linked to a company, saving auto-creates/updates a DSC renewal for it</p>
                </div>
                <div style={{ gridColumn:'span 3', background: darkMode?'rgba(59,130,246,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p style={{ fontSize:12, color:'#2563eb', margin:0 }}><strong>Fetch from MCA</strong> — Coming soon. Auto-verify DIN status directly from MCA portal.</p>
                </div>
              </div>

              <SectionHeader num="5" title="Bank Details" T={T} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Bank Name</label>
                  <input type="text" placeholder="ICICI Bank, Ashok Marg" value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Account Number</label>
                  <input type="text" placeholder="Account number" value={form.bank_account} onChange={e=>setForm(f=>({...f,bank_account:e.target.value}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>IFSC Code</label>
                  <input type="text" placeholder="ICIC0001234" maxLength={11} value={form.bank_ifsc} onChange={e=>setForm(f=>({...f,bank_ifsc:e.target.value.toUpperCase()}))} style={is} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Account Type</label>
                  <select value={form.bank_account_type} onChange={e=>setForm(f=>({...f,bank_account_type:e.target.value}))} style={is}>
                    <option value="">Select...</option>
                    <option>Savings</option><option>Current</option><option>Salary</option><option>NRE</option><option>NRO</option>
                  </select>
                </div>
              </div>

              <SectionHeader num="6" title="Notes" T={T} />
              <textarea rows="3" placeholder="Any additional remarks or information about this person..."
                value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{ ...is, resize:'none' }} />

            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:12, padding:'16px 32px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setShowForm(false)} style={{ padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 28px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Saving...' : editPerson ? 'Save Changes' : 'Add Person'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT MODAL ── */}
      {showImport && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:600, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 28px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, background:'rgba(255,255,255,0.1)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {Icons.import}
                </div>
                <div>
                  <h3 style={{ fontSize:14, fontWeight:700, color:'#fff', margin:0 }}>Import People</h3>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'2px 0 0' }}>Bulk upload from Excel</p>
                </div>
              </div>
              <button onClick={()=>{ setShowImport(false); setImportRows([]); setImportResult(null); }}
                style={{ width:32, height:32, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <style>{`
              @keyframes people-import-spin { to { transform: rotate(360deg); } }
              @keyframes people-import-stripe { from { background-position: 0 0; } to { background-position: 28px 0; } }
            `}</style>

            <div style={{ padding:28, display:'flex', flexDirection:'column', gap:16, background:T.bg3 }}>
              {!importing && !(importResult && !importResult.message) && (<>
              {/* Step 1 */}
              <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:20 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:40, height:40, background: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(22,163,74,0.2)':'#bbf7d0'}`, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#16a34a' }}>
                      {Icons.download}
                    </div>
                    <div>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>Download Template</p>
                      <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>Pre-formatted Excel with all required columns + sample row</p>
                    </div>
                  </div>
                  <button onClick={downloadTemplate} style={{
                    display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer',
                    border:`1px solid ${darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'}`, background: darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', color:'#16a34a',
                  }}>
                    {Icons.download} Download
                  </button>
                </div>
              </div>

              {/* Warning */}
              <div style={{ background: darkMode?'rgba(217,119,6,0.1)':'#fffbeb', border:`1px solid ${darkMode?'rgba(217,119,6,0.25)':'#fde68a'}`, borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:10 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" style={{ flexShrink:0, marginTop:1 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <p style={{ fontSize:12, color: darkMode?'#fbbf24':'#92400e', lineHeight:1.5, margin:0 }}>
                  <strong>Important:</strong> Client names must exactly match the client name in ChanakyaX (create clients first). For one person linked to multiple companies — e.g. Director in 4 companies, shareholder in 2 of them — add <strong>one row per company</strong> using the <strong>same PAN</strong> each time; that updates the one person record and adds/updates only that company's link (no duplicates). Leave "Shareholding %" blank on rows where they don't hold shares.
                </p>
              </div>

              {/* Upload */}
              <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:20 }}>
                <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:'0 0 12px' }}>Upload Filled Template</p>
                <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'100%', height:112, border:`2px dashed ${importRows.length>0?'#3b82f6':T.border2}`, borderRadius:12, cursor:'pointer', background: importRows.length>0?(darkMode?'rgba(59,130,246,0.08)':'#eff6ff'):'transparent', transition:'all 0.2s' }}
                  onMouseEnter={e=>{ if(!importRows.length) e.currentTarget.style.borderColor='#3b82f6'; }}
                  onMouseLeave={e=>{ if(!importRows.length) e.currentTarget.style.borderColor=T.border2; }}>
                  <input type="file" ref={importFileRef} accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleImportFile} />
                  {importRows.length > 0 ? (
                    <div style={{ textAlign:'center' }}>
                      <svg style={{ display:'block', margin:'0 auto 8px' }} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <p style={{ fontSize:13, fontWeight:600, color:'#2563eb', margin:0 }}>{importRows.length} rows ready to import</p>
                      <p style={{ fontSize:11, color:'#3b82f6', marginTop:3 }}>Click to change file</p>
                    </div>
                  ) : (
                    <div style={{ textAlign:'center' }}>
                      <svg style={{ display:'block', margin:'0 auto 8px' }} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <p style={{ fontSize:13, fontWeight:500, color:T.text2, margin:0 }}>Click to upload Excel file</p>
                      <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>.xlsx or .xls only</p>
                    </div>
                  )}
                </label>
              </div>
              </>)}

              {/* Pre-flight error (file couldn't be read / unparseable dates) */}
              {importResult && importResult.message && (
                <div style={{ borderRadius:10, padding:'14px 16px', border:`1px solid ${darkMode?'rgba(220,38,38,0.3)':'#fecaca'}`, background: darkMode?'rgba(220,38,38,0.1)':'#fef2f2', display:'flex', alignItems:'flex-start', gap:10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" style={{ flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p style={{ fontSize:12, color:'#dc2626', lineHeight:1.5, margin:0 }}>{importResult.message}</p>
                </div>
              )}

              {/* Live progress */}
              {importing && importProgress && (() => {
                const preparing = importProgress.phase === 'preparing';
                const pct = importProgress.total ? Math.round((importProgress.current / importProgress.total) * 100) : 0;
                return (
                  <div style={{ borderRadius:14, border:`1px solid ${T.border}`, background:T.bg2, padding:20 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <span style={{ width:36, height:36, borderRadius:10, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <span style={{ width:16, height:16, border:'2px solid #3b82f6', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'people-import-spin 0.6s linear infinite' }}/>
                        </span>
                        <div>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{preparing ? 'Preparing import…' : 'Importing people'}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>Please keep this window open</p>
                        </div>
                      </div>
                      <p style={{ fontSize:18, fontWeight:800, color:T.text, margin:0, fontVariantNumeric:'tabular-nums' }}>
                        {importProgress.current}<span style={{ color:T.text3, fontWeight:600, fontSize:13 }}> / {importProgress.total}</span>
                      </p>
                    </div>

                    <div style={{ height:12, width:'100%', borderRadius:999, background: darkMode?'rgba(255,255,255,0.08)':'#eef1f6', overflow:'hidden' }}>
                      <div style={{
                        height:'100%', borderRadius:999,
                        width: `${preparing ? 8 : Math.max(pct, 2)}%`,
                        background: preparing ? 'linear-gradient(90deg,#cbd5e1,#e2e8f0)' : 'linear-gradient(90deg,#0f1f45,#2a4d82)',
                        transition:'width 0.35s ease-out', position:'relative', overflow:'hidden',
                      }}>
                        {!preparing && (
                          <div style={{
                            position:'absolute', inset:0,
                            backgroundImage:'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                            backgroundSize:'28px 28px', animation:'people-import-stripe 0.7s linear infinite',
                          }} />
                        )}
                      </div>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:12 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:16, fontSize:12, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>
                        <span style={{ color:'#16a34a' }}>✓ {importProgress.ok || 0} added</span>
                        <span style={{ color:'#d97706' }}>⊘ {importProgress.skip || 0} skipped</span>
                        <span style={{ color: importProgress.fail ? '#dc2626' : T.text3 }}>✕ {importProgress.fail || 0} failed</span>
                      </div>
                      <p style={{ fontSize:11, fontWeight:700, color:T.text3, margin:0, fontVariantNumeric:'tabular-nums' }}>{preparing ? '' : `${pct}%`}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Final report */}
              {importResult && !importResult.message && (
                <div style={{ borderRadius:14, border:`1px solid ${T.border}`, overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:`1px solid ${T.border}`, background:T.bg3 }}>
                    <span style={{ width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background: importResult.errors?.length ? (darkMode?'rgba(220,38,38,0.15)':'#fef2f2') : (darkMode?'rgba(22,163,74,0.15)':'#f0fdf4') }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={importResult.errors?.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Import complete</p>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)' }}>
                    {[
                      { label:'Imported', value: importResult.success || 0, color:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4' },
                      { label:'Skipped',  value: importResult.skipped || 0, color:'#d97706', bg: darkMode?'rgba(217,119,6,0.12)':'#fffbeb' },
                      { label:'Failed',   value: importResult.errors?.length || 0, color:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2' },
                    ].map((s,idx) => (
                      <div key={s.label} style={{ padding:16, textAlign:'center', background: s.value ? s.bg : T.bg2, borderLeft: idx>0 ? `1px solid ${T.border}` : 'none' }}>
                        <p style={{ fontSize:22, fontWeight:800, color: s.value ? s.color : T.text3, margin:0, fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                        <p style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', color:T.text3, margin:'2px 0 0' }}>{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {(importResult.errors?.length > 0 || importResult.warnings?.length > 0) && (
                    <div style={{ borderTop:`1px solid ${T.border}`, padding:14, display:'flex', flexDirection:'column', gap:5, maxHeight:160, overflowY:'auto', background:T.bg3 }}>
                      {importResult.errors?.map((e,i)=>(
                        <p key={'e'+i} style={{ fontSize:12, color:'#dc2626', margin:0, display:'flex', gap:6 }}><span style={{ flexShrink:0 }}>✕</span><span>{e}</span></p>
                      ))}
                      {importResult.warnings?.map((w,i)=>(
                        <p key={'w'+i} style={{ fontSize:12, color:T.text3, margin:0, display:'flex', gap:6 }}><span style={{ flexShrink:0, color:'#d97706', fontWeight:700 }}>!</span><span>{w}</span></p>
                      ))}
                    </div>
                  )}
                  <div style={{ borderTop:`1px solid ${T.border}`, padding:'10px 14px', background:T.bg2 }}>
                    <p style={{ fontSize:11, color:T.text3, margin:0 }}>
                      {importResult.success > 0
                        ? `${importResult.success} of ${importResult.total} row${importResult.total===1?'':'s'} added to People.`
                        : 'No new people were added.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:12, padding:'16px 28px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>{ if(importing) return; setShowImport(false); setImportRows([]); setImportResult(null); }} disabled={importing}
                style={{ padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor: importing?'not-allowed':'pointer', opacity: importing?0.5:1 }}
                onMouseEnter={e=>{ if(!importing) e.currentTarget.style.background=T.bg3; }}
                onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>{importResult && !importResult.message ? 'Done' : 'Close'}</button>
              {importRows.length > 0 && (!importResult || importResult.message) && (
                <button onClick={handleImportSubmit} disabled={importing} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 24px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:importing?0.6:1 }}>
                  {importing && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'people-import-spin 0.6s linear infinite' }}/>}
                  {importing
                    ? (importProgress ? `Importing… ${importProgress.current}/${importProgress.total}` : 'Importing...')
                    : importResult?.message ? 'Retry Import' : `Import ${importRows.length} Rows`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default People;